import { Topbar } from "../components/Topbar";
import { KpiCard } from "../components/KpiCard";
import { Panel } from "../components/Panel";
import { LoadingState, ErrorState } from "../components/States";
import { useDashboard } from "../hooks/useDashboard";
import { formatCurrency, formatHours, formatMonthLabel, formatNumber, formatPct } from "../lib/dates";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Legend,
} from "recharts";

const chartColors = ["#002FA7", "#3B82F6", "#10B981", "#F59E0B"];

export default function Overview() {
  const { data, loading, error } = useDashboard("overview");

  return (
    <>
      <Topbar title="Resumen ejecutivo" subtitle="Visión transversal de los módulos clave" />
      <div className="p-6 space-y-6" data-testid="overview-page">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
              <KpiCard testId="kpi-revenue" label="Ingresos" value={formatCurrency(data.kpis.revenue)} sublabel="Facturación emitida" />
              <KpiCard testId="kpi-expense" label="Gastos" value={formatCurrency(data.kpis.expense)} sublabel="Facturas recibidas" />
              <KpiCard testId="kpi-margin" label="Margen" value={formatCurrency(data.kpis.margin)} accent={data.kpis.margin >= 0 ? "success" : "danger"} />
              <KpiCard testId="kpi-sales" label="Ventas" value={formatCurrency(data.kpis.sales_amount)} sublabel={`${formatNumber(data.kpis.sales_orders, { maximumFractionDigits: 0 })} pedidos`} />
              <KpiCard testId="kpi-purchases" label="Compras" value={formatCurrency(data.kpis.purchase_amount)} sublabel={`${formatNumber(data.kpis.purchase_orders, { maximumFractionDigits: 0 })} órdenes`} />
              <KpiCard testId="kpi-hours" label="Horas imputadas" value={formatHours(data.kpis.total_hours)} sublabel={`${formatPct(data.kpis.billable_pct)} facturables`} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard testId="kpi-projects-count" label="Proyectos" value={formatNumber(data.kpis.projects_count, { maximumFractionDigits: 0 })} sublabel={`${data.kpis.active_projects} activos`} />
              <KpiCard testId="kpi-open-ar" label="Cobros pendientes" value={formatCurrency(data.kpis.open_ar)} accent="warning" />
              <KpiCard testId="kpi-open-ap" label="Pagos pendientes" value={formatCurrency(data.kpis.open_ap)} accent="warning" />
              <KpiCard testId="kpi-margin-pct" label="Margen %" value={data.kpis.revenue ? formatPct((data.kpis.margin / data.kpis.revenue) * 100) : "—"} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Panel title="Ingresos vs Gastos" subtitle="Evolución mensual" testId="chart-revenue-expense">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.revenue_expense_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={{ fontSize: 11, fill: "#64748B" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => formatCurrency(v)} />
                      <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={formatMonthLabel} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Area type="monotone" dataKey="ingresos" name="Ingresos" stroke="#002FA7" fill="#002FA7" fillOpacity={0.15} />
                      <Area type="monotone" dataKey="gastos" name="Gastos" stroke="#EF4444" fill="#EF4444" fillOpacity={0.1} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Panel>

              <Panel title="Ventas vs Compras" subtitle="Importe mensual (sin impuestos)" testId="chart-sales-purchases">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={(data.sales_trend || []).map((s, i) => ({
                      month: s.month,
                      ventas: s.total,
                      compras: (data.purchase_trend?.[i]?.total) || 0,
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={{ fontSize: 11, fill: "#64748B" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => formatCurrency(v)} />
                      <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={formatMonthLabel} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="ventas" name="Ventas" fill="#002FA7" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="compras" name="Compras" fill="#94A3B8" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Panel title="Top 5 clientes" testId="panel-top-customers">
                <div className="space-y-2">
                  {data.top_customers.map((c, i) => (
                    <div key={i} className="flex justify-between items-center text-sm border-b border-slate-100 pb-2 last:border-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center text-[11px] font-semibold text-slate-500 shrink-0">{i + 1}</div>
                        <span className="truncate text-slate-700">{c.customer}</span>
                      </div>
                      <span className="tabular-nums font-medium text-slate-900">{formatCurrency(c.amount)}</span>
                    </div>
                  ))}
                  {!data.top_customers.length && <div className="text-xs text-slate-400 py-6 text-center">Sin datos</div>}
                </div>
              </Panel>

              <Panel title="Top 5 proveedores" testId="panel-top-suppliers">
                <div className="space-y-2">
                  {data.top_suppliers.map((s, i) => (
                    <div key={i} className="flex justify-between items-center text-sm border-b border-slate-100 pb-2 last:border-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center text-[11px] font-semibold text-slate-500 shrink-0">{i + 1}</div>
                        <span className="truncate text-slate-700">{s.supplier}</span>
                      </div>
                      <span className="tabular-nums font-medium text-slate-900">{formatCurrency(s.amount)}</span>
                    </div>
                  ))}
                  {!data.top_suppliers.length && <div className="text-xs text-slate-400 py-6 text-center">Sin datos</div>}
                </div>
              </Panel>
            </div>

            <Panel title="Horas imputadas" subtitle="Evolución mensual" testId="chart-hours-trend">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.hours_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={{ fontSize: 11, fill: "#64748B" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                    <Tooltip formatter={(v) => formatHours(v)} labelFormatter={formatMonthLabel} />
                    <Bar dataKey="hours" name="Horas" fill={chartColors[0]} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </>
        )}
      </div>
    </>
  );
}
